import { useRef, useState } from "react";
import { useLive, del } from "./api";
import type { Attachment } from "./api";

/**
 * Tira de fotos com upload, usada em dois donos diferentes.
 *
 * Nasceu na tela de imoveis e foi extraida quando a base de conhecimento
 * passou a aceitar foto tambem. Sao os mesmos gestos -- arrastar, ver a capa,
 * remover -- e duas copias divergiriam na primeira correcao.
 */
export function Fotos({
  base,
  onChange,
  dica,
}: {
  /** Prefixo da rota: `/api/properties/<id>` ou `/api/knowledge/<id>`. */
  base: string;
  onChange?: () => void;
  dica?: string;
}) {
  const { data: fotos, reload } = useLive<Attachment[]>(`${base}/photos`, [
    "attachments",
  ]);
  const input = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(files: FileList | null) {
    if (!files?.length) return;
    setEnviando(true);
    setErro(null);
    try {
      const form = new FormData();
      for (const f of files) form.append("foto", f);
      const res = await fetch(`${base}/photos`, { method: "POST", body: form });
      const r = (await res.json()) as { error?: string };
      if (r.error) setErro(r.error);
      else {
        reload();
        onChange?.();
      }
    } catch {
      setErro("não deu para enviar — a API respondeu?");
    } finally {
      setEnviando(false);
      if (input.current) input.current.value = "";
    }
  }

  return (
    <div
      className="space-y-1.5"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        void enviar(e.dataTransfer.files);
      }}
    >
      <div className="flex flex-wrap gap-2">
        {fotos?.map((f, i) => (
          <div key={f.id} className="relative group">
            <img
              src={`/media/${f.id}`}
              alt={f.filename}
              className="w-20 h-20 object-cover rounded-lg border border-rule"
            />
            {i === 0 && (
              <span className="absolute top-1 left-1 text-[9px] bg-accent text-ground px-1 rounded-lg">
                capa
              </span>
            )}
            {i === 3 && (
              <span
                className="absolute inset-0 bg-ground/70 rounded-lg flex items-center
                  justify-center text-[11px] text-ink-3 text-center px-1"
                title="a IA envia no máximo 3 por vez"
              >
                a partir daqui
                <br />
                não vai
              </span>
            )}
            <button
              onClick={async () => {
                await del(`/attachments/${f.id}`);
                reload();
                onChange?.();
              }}
              className="absolute top-1 right-1 w-5 h-5 rounded-lg bg-danger text-ground
                text-xs opacity-0 group-hover:opacity-100 transition-opacity"
              title="remover"
            >
              ×
            </button>
          </div>
        ))}

        <button
          onClick={() => input.current?.click()}
          disabled={enviando}
          className="w-20 h-20 border border-dashed border-rule-2 rounded-lg
            text-xs text-ink-3 hover:border-accent hover:text-accent
            transition-colors disabled:opacity-50"
        >
          {enviando ? "…" : "+ fotos"}
        </button>
        <input
          ref={input}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
          onChange={(e) => void enviar(e.target.files)}
          className="hidden"
        />
      </div>

      <p className="text-[11px] text-ink-3">
        {dica ?? (
          <>
            Arraste aqui. A <span className="text-accent">primeira é a capa</span>{" "}
            — leva a legenda no WhatsApp. JPG, PNG ou WEBP até 8MB.
          </>
        )}
      </p>
      {erro && <p className="text-xs text-danger">{erro}</p>}
    </div>
  );
}
